import { useNavigate } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { QuotationFormData } from "../../../shared/schemas/quotation.schema";
import { trpcClient } from "@/lib/trpc";
import { useQuotationStore } from "@/stores/useQuotationStore";
import { useAuthStore } from "@/stores/auth.store";
import QuotationForm from "./QuotationForm";
import { toast } from "sonner";
import StandardDetailLayout from "@/layouts/StandardDetailLayout";

import { addDaysToDateString, toVnDateString } from "@apv-erp/status";
// ID is auto-generated by the backend.

// Add days to date string
function addDays(dateStr: string, days: number): string {
  return addDaysToDateString(toVnDateString(dateStr), days);
}

export default function CreateQuotationPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const currentUser = useAuthStore((s) => s.user);
  const quotations = useQuotationStore((s) => s.quotations);
  const addQuotation = useQuotationStore((s) => s.addQuotation);

  const today = toVnDateString();
  const autoValidUntil = addDays(today, 30);

  const handleSubmit = async (data: QuotationFormData) => {
    try {
      const trpcPayload = {
        ...data,
        version: 1,
        approvedDate: null,
        creatorId: data.creatorId || currentUser?.id || null,
        customerId: data.customerId || null,
        customerName: data.customerName || null,
        vatRate: data.vatRate ?? null,
        deliveryAddress: data.deliveryAddress || null,
        contactPerson: data.contactPerson || null,
        phone: data.phone || null,
        paymentTerm: data.paymentTerm || null,
        deliveryTime: data.deliveryTime || null,
        notes: data.notes || null,
        details: data.details?.map((d) => ({
          productId: d.productId || null,
          productCode: d.productCode || null,
          productName: d.productName || "",
          specs: d.specs || null,
          unit: d.unit || null,
          process: (d as any).process || null,
          quantity: Number(d.quantity) || 0,
          priceVND: Number(d.priceVND) || Number(d.quotedPrice) || 0,
          quotedPrice: d.quotedPrice ? Number(d.quotedPrice) : (d.priceVND ? Number(d.priceVND) : null),
          priceUSD: d.priceUSD ? Number(d.priceUSD) : null,
          costPrice: d.costPrice ? Number(d.costPrice) : null,
          profitMargin: d.profitMargin ? Number(d.profitMargin) : null,
          notes: d.notes || null,
        })),
      };
      delete (trpcPayload as any).quotationId;

      const res = await trpcClient.quotations.create.mutate(trpcPayload as any);

      await queryClient.invalidateQueries({ queryKey: ["quotations"] });
      navigate({ to: `/quotations/${res.id}` });
    } catch (err: any) {
      console.warn(
        "[QuotationCreate] tRPC failed, falling back to store:",
        err?.message,
      );
      const fallbackData = { ...data };
      if (!fallbackData.quotationId) {
        fallbackData.quotationId = `BG${new Date().getFullYear()}-${String(Math.floor(Math.random() * 1000)).padStart(3, "0")}`;
      }
      const ok = await addQuotation(fallbackData as any);
      if (ok) {
        navigate({ to: "/quotations" });
      } else {
        toast.error(err?.message || "Tạo báo giá thất bại");
      }
    }
  };

  return (
    <StandardDetailLayout
      breadcrumbs={[
        { label: "Báo giá", to: "/quotations" },
        { label: "Tạo mới" },
      ]}
      title="Tạo Báo Giá Mới"
      onBack={() => navigate({ to: "/quotations" })}
      showContextPanel={false}
    >
      <div className="w-full space-y-4 animate-in fade-in duration-300">
        <QuotationForm
          defaultValues={{
            creatorId: currentUser?.id || "",
            createdDate: today,
            validUntil: autoValidUntil,
            status: "DRAFT",
            vatRate: 8,
          }}
          onSubmit={handleSubmit}
          onCancel={() => navigate({ to: "/quotations" })}
          submitLabel="Lưu"
        />
      </div>
    </StandardDetailLayout>
  );
}
